import {
  type Dispatch,
  type FC,
  memo,
  type SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { AppRoute } from '@common/app/routes';
import { calcToProduceValue } from '@common/lib/calcToProduceValue';
import { ProductionButton } from '@components/Plant/ProductionButtons/ProductionButtons';
import { ToUpgrade } from '@components/ToUpgrade/ToUpgrade';
import { WorkshopContext } from '@pages/Workshop/context';
import { changeStorage } from '@redux/reducers/userReducer';
import {
  selectTotalWorkshopBoostById,
  selectWorkshopByNameOrId,
} from '@redux/selectors/workshopSelectors';
import { useAppDispatch, useAppSelector } from '@redux/store';
import { calculateInstantCompleteValue } from '@shared-libs/index';

import {
  useClaimGoodsByIdMutation,
  usePutToWorkshopMutation,
} from '@/api/queries/workshop';
import { ToastNotification } from '@/common/components/ToastNotification/ToastNotification';
import { gameItemOrder } from '@/common/constants';
import { useCheckStorageOverflow } from '@/common/hooks/check-storage-overflow';
import { calcProductionCost } from '@/common/lib/calcProductionCost';
import { divide, multiplication } from '@/common/lib/decimal';
import {
  firebaseLogCollectResourceWorkshop,
  firebaseLogResourceProcessing,
} from '@/common/lib/firebaseLog';
import { playSound } from '@/common/lib/soundEffect';
import { ModalContext } from '@/components/Modals/Modal/config';
import { ProductionScale } from '@/components/Plant/ProductionScale/ProductionScale';
import { StringXSmall, TextXSmall } from '@/components/Typography/Typography';

import { BoostWorkshopButton } from '../BoostWorkshopButton/BoostWorkshopButton';

import { ProducePercentage } from './ProducePercentage/ProducePercentage';

import stockPng from '/img/tabbar/office.png';
import stockWebp from '/img/tabbar/office.webp';

// import css from './styles.module.scss';

interface WorkshopProductionProps {
  id: string;
  openBoostFactory: () => void;
  isComplete: boolean;
  incommingStorage: number;
  toAdd: number;
  setToAdd: Dispatch<SetStateAction<number>>;
  producted: number;
  localStock: number;
  activeIsTarget: boolean;
  limit?: number;
  max: number;
}

const WorkshopProduction: FC<WorkshopProductionProps> = memo(
  ({
    id,
    openBoostFactory,
    isComplete,
    limit,
    toAdd,
    producted,
    incommingStorage,
    setToAdd,
    localStock,
    activeIsTarget,
    max,
  }) => {
    const { setBooostProductionDynamicData } = useContext(ModalContext);
    const { activeGoodsName, activeGoods, targetProduct } =
      useContext(WorkshopContext);
    const { t } = useTranslation();
    const [percentage, setPercentage] = useState(100);
    const totalBoost = useAppSelector((state) =>
      selectTotalWorkshopBoostById(state, id),
    );
    const [claimGoods, claimGoodsApiState] = useClaimGoodsByIdMutation();
    const [putToWorkshop, putToWorkshopApiState] = usePutToWorkshopMutation();
    const checkStockOverflow = useCheckStorageOverflow();
    const workshop = useAppSelector((state) =>
      selectWorkshopByNameOrId(state, id),
    );
    const dispatch = useAppDispatch();
    const setToProduction = useCallback(
      (percentage: number): void => {
        if (limit) {
          const toAdd = calcToProduceValue({
            percentage,
            limit,
            incommingStorage,
            localStock,
            max,
          });
          setToAdd(toAdd);
        }
      },
      [limit, incommingStorage, localStock, max],
    );

    useEffect(() => {
      setPercentage(100);
    }, [id, activeGoodsName, incommingStorage]);

    // const remainsToProduce = Math.max(incommingStorage - producted, 0);

    const availableToProduction =
      activeIsTarget || (localStock === 0 && incommingStorage === 0);

    useEffect(() => {
      if (activeGoods?.productionRateOriginal !== undefined) {
        const inPool = incommingStorage - producted;
        const hoursToFinishProduction = divide(
          inPool,
          activeGoods.productionRateOriginal,
        );
        const secondsToFinishProdution = multiplication(
          hoursToFinishProduction,
          3600,
        );
        setBooostProductionDynamicData({
          inPool: incommingStorage - producted,
          price: calculateInstantCompleteValue(secondsToFinishProdution),
        });
      }
    }, [
      producted,
      incommingStorage,
      activeGoods?.productionRateOriginal,
      setBooostProductionDynamicData,
    ]);

    const onClaim = (): void => {
      playSound('buttons/claim');
      claimGoods({ id })
        .unwrap()
        .then(({ storage }) => {
          checkStockOverflow(storage, gameItemOrder.goods);
          ToastNotification(t('Goods sent to Stock'), {
            img: stockPng,
            webp: stockWebp,
          });
        });

      if (workshop) {
        firebaseLogCollectResourceWorkshop(workshop, producted);
      }
    };

    const addToProduction = (): void => {
      if (activeGoods) {
        playSound(`produce/${workshop?.name}`);
        putToWorkshop({
          plantId: id,
          product: activeGoods.name,
          amount: toAdd,
        })
          .unwrap()
          .then(() => {
            setToAdd(0);
            const payload = calcProductionCost({
              toAdd,
              recipeForProduction: activeGoods.recipeForProduction,
            });

            if (workshop) {
              firebaseLogResourceProcessing(workshop, payload);
            }
            dispatch(changeStorage(payload));
          });
      }
    };

    return (
      <div className="w-full flex flex-col gap-4">
        <div className="flex items-center gap-4">
          <ToUpgrade
            link={AppRoute.WORKSHOP + `/${id}/upgrade`}
            isComplete={isComplete}
          />
          {activeIsTarget ? (
            <div className="relative flex-1 h-12">
              {!!toAdd && (
                <StringXSmall
                  weight="medium"
                  className="absolute bottom-full left-1/2 -translate-x-1/2 -translate-y-1/2"
                >
                  {`${t('Total')} +${toAdd + (incommingStorage - producted)}`}
                </StringXSmall>
              )}
              <ProductionScale
                activeIsTarget={activeIsTarget}
                toAdd={toAdd}
                limit={limit}
                localStock={localStock}
                incommingStorage={incommingStorage}
                productionTimeLeft={workshop?.productionTimeLeft}
              />
            </div>
          ) : (
            <TextXSmall
              weight="regular"
              className="!text-white/60 text-center !leading-1.2"
            >
              {t(
                'To add another item to the production, wait for the production to complete or clear the queue.',
              )}
            </TextXSmall>
          )}
          <BoostWorkshopButton
            totalBoost={totalBoost || 0}
            onClick={openBoostFactory}
            plantId={id}
          />
        </div>
        <ProducePercentage
          onClick={setToProduction}
          percentage={percentage}
          setPercentage={setPercentage}
        />
        <ProductionButton
          claimButtonProps={{
            onClick: onClaim,
            disabled:
              !(localStock + producted) ||
              claimGoodsApiState.isLoading ||
              !activeIsTarget,

            inactive: !(localStock + producted) || !activeIsTarget,
            isLoading: claimGoodsApiState.isLoading,
          }}
          produceButtonProps={{
            onClick: addToProduction,
            disabled:
              !toAdd ||
              putToWorkshopApiState.isLoading ||
              !availableToProduction,
            inactive: !toAdd || !availableToProduction,
            isLoading: putToWorkshopApiState.isLoading,
          }}
          localStock={localStock}
          toAdd={toAdd}
          itemName={targetProduct || activeGoodsName}
          producted={producted}
          recipe={activeGoods?.recipeForProduction}
        />
      </div>
    );
  },
);

export { WorkshopProduction };
